import React, { useState, useEffect, useRef } from 'react';
import './BottomSection.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import database from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import ReactCardFlip from 'react-card-flip';
import Lottie from 'lottie-react';

const l = ['A', 'B', 'C', 'D'];

// Helper function to convert emoji to hex code for Noto Emoji Animation
function emojiToHexCode(emoji) {
  if (!emoji) return null;
  
  try {
    // Get just the first code point of the emoji as hex
    const codePoint = emoji.codePointAt(0);
    if (!codePoint) return null;
    
    // Format as the expected hex code (just the base code point)
    return codePoint.toString(16).toLowerCase();
  } catch (error) {
    console.error('Error converting emoji to hex:', error);
    return null;
  }
}

function BottomSection({ mode, contest, channelId, gameSound }) {
  const [players, setPlayers] = useState([]);
  const prevPlayersRef = useRef([]);
  const [emojiAnimations, setEmojiAnimations] = useState({});

  // Function to fetch emoji animation data
  const fetchEmojiAnimation = async (emoji) => {
    try {
      if (!emoji) return null;
      
      // Convert emoji to hex code format for Noto Emoji Animation
      const hexCode = emojiToHexCode(emoji);
      if (!hexCode) return null;
      
      // Use the correct format for Noto Emoji API
      const url = `https://fonts.gstatic.com/s/e/notoemoji/latest/${hexCode}/lottie.json`;
    
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log(`Failed to fetch emoji animation for ${emoji} (${hexCode}): ${response.status}`);
        
        // Fallback to alternative emoji display if animation is not available
        return null;
      }
      
      const animationData = await response.json();
      return animationData;
    } catch (error) {
      console.error("Error fetching emoji animation:", error);
      return null;
    }
  };

  useEffect(() => {
    const roomId = channelId || "_main";
    const playersRef = ref(database, `rooms/${roomId}/players`);

    const handleData = (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const playersList = Object.values(data).filter(player => !player.inactive);
        
        if (prevPlayersRef.current.length > 0) {
          playersList.forEach(player => {
            const prevPlayer = prevPlayersRef.current.find(p => p.userid === player.userid);
            if (prevPlayer && 
                typeof player.optionIndexSelected === 'number' && 
                typeof prevPlayer.optionIndexSelected !== 'number') {
              if (gameSound) {
                gameSound.answer.play();
              }
            }
          });
        }
        
        prevPlayersRef.current = playersList;
        setPlayers(playersList);

        // Load emoji animations for new players
        playersList.forEach(player => {
          if (player.emoji && !emojiAnimations[player.emoji]) {
            fetchEmojiAnimation(player.emoji)
              .then(animationData => {
                if (animationData) {
                  setEmojiAnimations(prev => ({
                    ...prev,
                    [player.emoji]: animationData
                  }));
                }
              });
          }
        });
      } else {
        prevPlayersRef.current = [];
        setPlayers([]);
      }
    };

    onValue(playersRef, handleData, (error) => {
      console.error("Error fetching players:", error);
    });

    return () => {
      off(playersRef, 'value', handleData);
    };
  }, [channelId, gameSound, emojiAnimations]);

  return (
    <>
      {mode !== 0 && (
        <div className="bottom-section" style={{ flexDirection: 'row' }}>
          <div className="center">
            <div className="avatar-container">
              {players.map((player) => {
                const avatarStyle = player.avatar ? {
                  backgroundImage: `url(${player.avatar})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                } : {};

                const hasAnswered = typeof player.optionIndexSelected === 'number';
                const isCorrect = player.status === "correct";
                const initials = (player.username || player.name || 'AN').slice(0, 2).toUpperCase();
                const reveal = (mode === 3 || mode === 4 || mode === 5);
                const noAvatar = !!player.avatar;

                let borderColor = 'rgba(255,255,255,0)';
                if (hasAnswered) {
                  borderColor = 'yellow';
                  if (reveal) {
                    if (isCorrect) {
                      borderColor = '#00E096';
                    } else {
                      borderColor = 'red';
                    }
                  }
                }
                avatarStyle.borderColor = borderColor;

                const isNewPlayer = !prevPlayersRef.current.some(p => p.userid === player.userid);
                const isLeavingPlayer = !players.some(p => p.userid === player.userid);

                return (
                  <div 
                    key={player.userid} 
                    className={`avatar-wrapper ${isNewPlayer ? 'new-player' : ''} ${isLeavingPlayer ? 'leaving-player' : ''}`}
                  >
                    <div 
                      className={`unanswered-avatar ${hasAnswered && !reveal ? 'rotating-border' : ''}`} 
                      style={avatarStyle}
                    >
                      <span className="avatar-initials">{!noAvatar ? initials : ''}</span>
                      <span className="streak-badge">
                        {player.emoji && emojiAnimations[player.emoji] ? (
                          <div className="animated-emoji">
                            <Lottie 
                              animationData={emojiAnimations[player.emoji]} 
                              loop={true}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                        ) : (
                          player.emoji || ''
                        )}
                      </span>
                      {(mode === 3 || mode === 4) && (
                        <span 
                          className="a-badge" 
                          style={{ backgroundColor: borderColor }}
                        >
                          {hasAnswered ? l[player.optionIndexSelected] : ''}
                        </span>
                      )}
                    </div>
                    <span className="avatar-name">{player.username || player.name || 'Anonymous'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BottomSection;